import type { ProjectMapFeatureResponse, ProjectMapStyle, ProjectMapSymbol, PublishedMapLayer } from '../services/project-map-manifest.service';
import { boundsIntersect, MapBounds, validMapBounds } from './project-map-inspection';

/**
 * Live drawing of published layers backed by a live source (ArcGIS FeatureServer/MapServer).
 *
 * The api answers `GET …/map-layers/:layerId/features` live-first (`source: 'live'`, snapshot fallback), so
 * picking already sees features added after the last capture. Drawing comes from the published snapshot
 * assets (GeoJSON or raster tiles) and would not show them. For such layers the map draws the live
 * features of the current view instead, and falls back to the snapshot drawing whenever the live answer
 * is unavailable, truncated or out of range. Pure helpers here (no ArcGIS) so they run under karma.
 */

/** One page of the features endpoint (api MAX_LIMIT); a fuller view falls back to the snapshot drawing. */
export const LIVE_FEATURE_LIMIT = 200;
/** api MAX_BBOX_WIDTH / MAX_BBOX_HEIGHT (degrees). */
export const LIVE_MAX_BBOX_DEGREES = 1;
/** Extent changes are debounced so a pan or zoom asks once it settles. */
export const LIVE_REFRESH_DEBOUNCE_MS = 400;
/** The query reaches this fraction past each side of the view, so a short pan has features ready. */
const VIEW_PADDING = 0.25;

/**
 * The sublayerKey the ArcGIS ingest gives a layer ("arcgis:{layerId}", the api's ARCGIS_SUBLAYER): the only live-source
 * signal the manifest exposes. Whether the source answers live is still the features response's `source`.
 */
const LIVE_SUBLAYER = /^arcgis:\d{1,5}$/;

/**
 * Layers worth a live attempt: a live-source (ArcGIS) sublayer the map can draw as vectors (no dash styles,
 * which only the pinned raster renderer reproduces) and that is open to inspection (the endpoint serves it).
 */
export function liveDrawEligible(published: PublishedMapLayer | null | undefined): boolean {
  if (!published || published.inspect !== 'full' || !validMapBounds(published.bounds)) return false;
  if (typeof published.sublayerKey !== 'string' || !LIVE_SUBLAYER.test(published.sublayerKey)) return false;
  const symbols = [...(published.style?.symbols || []), ...(published.style?.uniqueValues?.values.map(value => value.symbol) || [])];
  return symbols.every(symbol => !symbol?.dash?.length);
}

/**
 * The bbox to ask for: the view (WGS84) padded a little, clamped to the api's 1° limit around the view
 * centre. Null below the layer's minZoom, when the view is wider than the limit, or off the layer's bounds:
 * the snapshot drawing covers those.
 */
export function liveQueryBBox(view: MapBounds | null | undefined, published: PublishedMapLayer, zoom: number): MapBounds | null {
  if (!view || !view.every(Number.isFinite) || !Number.isFinite(zoom) || zoom < published.minZoom) return null;
  const width = view[2] - view[0];
  const height = view[3] - view[1];
  if (width <= 0 || height <= 0 || width > LIVE_MAX_BBOX_DEGREES || height > LIVE_MAX_BBOX_DEGREES) return null;
  const padX = Math.min(width * VIEW_PADDING, (LIVE_MAX_BBOX_DEGREES - width) / 2);
  const padY = Math.min(height * VIEW_PADDING, (LIVE_MAX_BBOX_DEGREES - height) / 2);
  const bbox: MapBounds = [
    Math.max(-180, view[0] - padX), Math.max(-85.051129, view[1] - padY),
    Math.min(180, view[2] + padX), Math.min(85.051129, view[3] + padY),
  ];
  return boundsIntersect(published.bounds, bbox) ? bbox : null;
}

export type LiveResponseVerdict =
  /** Draw these features live (and hide the snapshot geometry). */
  | 'live'
  /** Keep the snapshot drawing for this view; a later view may answer live again. */
  | 'fallback'
  /** The layer has no live source (snapshot without a fallback reason): stop asking. */
  | 'not-live';

/** The response belongs to the drawn version (the same ids inspection checks). */
export function responseMatchesLayer(response: ProjectMapFeatureResponse | null | undefined, published: PublishedMapLayer,
  catalogRevision: number): boolean {
  return !!response && Array.isArray(response.features) && response.catalogRevision === catalogRevision
    && response.layerId === published.id && response.sourceVersionId === published.sourceVersionId
    && response.sublayerKey === published.sublayerKey;
}

/**
 * Filtered drawing (a picker session's feature_source `where`) draws from the features endpoint whatever its source,
 * so the layer only needs to be served by it: open to inspection, with valid bounds.
 */
export function filteredDrawEligible(published: PublishedMapLayer | null | undefined): boolean {
  return !!published && published.inspect === 'full' && validMapBounds(published.bounds);
}

/** Pages a filtered drawing follows (`nextCursor`) before it draws what it has. */
export const FILTERED_MAX_PAGES = 5;

/** What a features response means for drawing. Ids must match the drawn version, exactly as inspection checks them. */
export function liveResponseVerdict(response: ProjectMapFeatureResponse | null | undefined, published: PublishedMapLayer,
  catalogRevision: number): LiveResponseVerdict {
  if (!response || !Array.isArray(response.features)) return 'fallback';
  if (response.source !== 'live') return response.source === 'snapshot' && !response.sourceFallbackReason ? 'not-live' : 'fallback';
  if (response.truncated !== false || response.nextCursor || response.features.length > LIVE_FEATURE_LIMIT) return 'fallback';
  if (response.catalogRevision !== catalogRevision || response.layerId !== published.id
    || response.sourceVersionId !== published.sourceVersionId || response.sublayerKey !== published.sublayerKey) return 'fallback';
  return 'live';
}

/** The published symbol for one feature: its unique value when the style has one, else the geometry's default. */
export function publishedSymbolFor(style: ProjectMapStyle | null | undefined, geometry: ProjectMapSymbol['geometry'],
  properties: Record<string, unknown> | null | undefined): ProjectMapSymbol | null {
  const unique = style?.uniqueValues;
  if (unique?.field) {
    const raw = properties?.[unique.field];
    if (raw !== null && raw !== undefined) {
      const match = unique.values.find(value => value.symbol?.geometry === geometry && String(value.value) === String(raw));
      if (match) return match.symbol;
    }
  }
  return style?.symbols?.find(symbol => symbol.geometry === geometry) || null;
}

/** GeoJSON geometry type → published symbol geometry; null for types the map does not draw. */
export function symbolGeometry(geoJsonType: unknown): ProjectMapSymbol['geometry'] | null {
  switch (geoJsonType) {
    case 'Point': case 'MultiPoint': return 'point';
    case 'LineString': case 'MultiLineString': return 'line';
    case 'Polygon': case 'MultiPolygon': return 'polygon';
    default: return null;
  }
}

/**
 * A feature filter for one layer's drawing (picker sessions only: the feature_source `where`). While set, the layer
 * never shows its unfiltered snapshot drawing: it draws the matching features of the view, or nothing.
 */
export interface LiveDrawFilter {
  matches(properties: Record<string, unknown>): boolean;
  /**
   * After each refresh: how many matching features the view has (0 → the picker's empty notice), or null when
   * nothing could be fetched for the view (error, out of range).
   */
  onDrawn?(matching: number | null): void;
  /** A status marker to draw a feature with instead of its layer symbol (launcher points); null = the layer symbol. */
  status?(featureId: string, properties: Record<string, unknown>): 'done' | 'partial' | 'pending' | null;
}

/** What the drawing controller needs from the map, kept abstract so the logic runs without ArcGIS. */
export interface LiveDrawPorts {
  /** The view now: WGS84 extent (null when it cannot be expressed), zoom, and whether the layer is shown at all. */
  view(): { extent: MapBounds | null; zoom: number; visible: boolean };
  fetch(bbox: MapBounds, limit: number, signal: AbortSignal, cursor?: string | null): Promise<ProjectMapFeatureResponse>;
  /** Draws the live features; false when any of them cannot be drawn (the snapshot is kept then). */
  drawLive(features: ProjectMapFeatureResponse['features']): boolean;
  /** Clears the live features and shows the snapshot drawing again. */
  showSnapshot(): void;
  setTimer(callback: () => void, ms: number): unknown;
  /**
   * Whether a snapshot drawing exists to fall back to (default true). Without one (its asset failed or is missing)
   * every answer of the endpoint is drawn, live or snapshot, exactly like a filtered drawing.
   */
  hasSnapshot?(): boolean;
  clearTimer(handle: unknown): void;
}

/**
 * Live-first drawing for one published layer: on each settled view it asks the features endpoint for the
 * view and draws the live answer, or keeps the snapshot drawing (error, truncated, out of range, snapshot
 * answer). A layer answered from the snapshot without a fallback reason has no live source: it stops asking.
 */
export class LiveFeatureDrawController {
  /** 'live' while live features are drawn in place of the snapshot geometry. */
  mode: 'snapshot' | 'live' = 'snapshot';
  stopped = false;
  private timer: unknown = null;
  private controller: AbortController | null = null;

  constructor(private readonly ports: LiveDrawPorts, private readonly published: PublishedMapLayer,
    private readonly catalogRevision: number, private readonly filter: LiveDrawFilter | null = null) {}

  /**
   * Filtered, or no snapshot drawing to fall back to: draw whatever the endpoint answers (live or snapshot, all pages
   * up to FILTERED_MAX_PAGES), or nothing. Otherwise only live answers replace the snapshot drawing.
   */
  private get drawsEveryAnswer(): boolean {
    return !!this.filter || this.ports.hasSnapshot?.() === false;
  }

  /** Call on every settled view change or visibility change; debounced. */
  schedule(): void {
    if (this.stopped) return;
    if (this.timer !== null) this.ports.clearTimer(this.timer);
    this.timer = this.ports.setTimer(() => { this.timer = null; void this.refresh(); }, LIVE_REFRESH_DEBOUNCE_MS);
  }

  async refresh(): Promise<void> {
    if (this.stopped) return;
    this.controller?.abort();
    this.controller = null;
    const view = this.ports.view();
    // A hidden layer keeps whatever it shows; the next visible, settled view asks again.
    if (!view.visible) return;
    const bbox = liveQueryBBox(view.extent, this.published, view.zoom);
    if (!bbox) { this.fallBack(); return; }
    if (this.drawsEveryAnswer) { await this.refreshFiltered(bbox); return; }
    const controller = new AbortController();
    this.controller = controller;
    let response: ProjectMapFeatureResponse;
    try {
      response = await this.ports.fetch(bbox, LIVE_FEATURE_LIMIT, controller.signal);
    } catch {
      if (controller.signal.aborted || this.stopped) return;
      this.fallBack();
      return;
    } finally {
      if (this.controller === controller) this.controller = null;
    }
    if (controller.signal.aborted || this.stopped) return;
    const verdict = liveResponseVerdict(response, this.published, this.catalogRevision);
    if (verdict === 'not-live') { this.fallBack(); this.stop(); return; }
    if (verdict === 'fallback' || !this.ports.drawLive(response.features)) { this.fallBack(); return; }
    this.mode = 'live';
  }

  /** Every matching feature of the view from the endpoint (live or snapshot answer), or nothing; never the snapshot drawing. */
  private async refreshFiltered(bbox: MapBounds): Promise<void> {
    const controller = new AbortController();
    this.controller = controller;
    const features: ProjectMapFeatureResponse['features'] = [];
    try {
      let cursor: string | null = null;
      for (let page = 0; page < FILTERED_MAX_PAGES; page++) {
        const response = await this.ports.fetch(bbox, LIVE_FEATURE_LIMIT, controller.signal, cursor);
        if (controller.signal.aborted || this.stopped) return;
        if (!responseMatchesLayer(response, this.published, this.catalogRevision)) { this.fallBack(); return; }
        features.push(...response.features.filter(feature => !this.filter || this.filter.matches(feature?.properties || {})));
        cursor = response.nextCursor;
        if (!cursor) break;
      }
    } catch {
      if (controller.signal.aborted || this.stopped) return;
      this.fallBack();
      return;
    } finally {
      if (this.controller === controller) this.controller = null;
    }
    if (!this.ports.drawLive(features)) { this.fallBack(); return; }
    this.mode = 'live';
    this.filter?.onDrawn?.(features.length);
  }

  stop(): void {
    this.stopped = true;
    this.controller?.abort();
    this.controller = null;
    if (this.timer !== null) this.ports.clearTimer(this.timer);
    this.timer = null;
  }

  /** Unfiltered: back to the snapshot drawing. Filtered: draw nothing (the snapshot would show non-matching features). */
  private fallBack(): void {
    if (this.drawsEveryAnswer) { this.ports.drawLive([]); this.mode = 'snapshot'; this.filter?.onDrawn?.(null); return; }
    if (this.mode === 'live') this.ports.showSnapshot();
    this.mode = 'snapshot';
  }
}
